<template>
  <div :class="bem.b()">
    <div :class="bem.e('header')">
      <div :class="bem.e('title')">{{ currentDate }}</div>
      <div :class="bem.e('button-group')">
        <cms-button @click="selectDate('prev-year')">上一年</cms-button>
        <cms-button @click="selectDate('prev-month')">上个月</cms-button>
        <cms-button @click="selectDate('today')">今天</cms-button>
        <cms-button @click="selectDate('next-month')">下个月</cms-button>
        <cms-button @click="selectDate('next-year')">下一年</cms-button>
      </div>
    </div>
    <div :class="bem.e('body')">
      <table :class="bemTable.b()" cellpadding="0" cellspacing="0">
        <thead>
          <tr>
            <th v-for="day of weekDays" :key="day">
              {{ day }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rid) of rows" :key="rid">
            <td
              v-for="(cell, cid) of row"
              :key="cid"
              :class="[bemDay.b(), getCellClass(cell)]"
              @click="handlePick(cell)"
            >
              <slot name="date-cell" :data="getSlotData(cell)">
                {{ cell.text }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createNamespace } from '@chenms-zj/utils/create'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { computed, ref } from 'vue'
import {
  CalendarDateCell,
  CalendarDateCellType,
  CalendarDateType,
  calendarEmits,
  calendarProps
} from './calendar'
const bem = createNamespace('calendar')
const bemTable = createNamespace('calendar-table')
const bemDay = createNamespace('calendar-day')
defineOptions({
  name: 'cms-calendar'
})

const props = defineProps(calendarProps)
const emit = defineEmits(calendarEmits)

const now = dayjs() // 当前时间
const date = computed(() => {
  if (!props.modelValue) {
    return now
  } else {
    return dayjs(props.modelValue)
  }
})

const firstDayOfWeek = dayjs().startOf('week').day()
// 头部
const weekMaping = [
  '星期日',
  '星期一',
  '星期二',
  '星期三',
  '星期四',
  '星期五',
  '星期六'
]
const weekDays = computed(() => {
  return [
    ...weekMaping.slice(firstDayOfWeek),
    ...weekMaping.slice(0, firstDayOfWeek)
  ]
})
console.log('firstDayOfWeek', firstDayOfWeek)
// 面板部分

const rows = computed(() => {
  // 根据用户提供的日期 计算歘42个来
  // 1. 获取本月的第一天是星期几
  let list: CalendarDateCell[] = []
  const firstDay = date.value.startOf('month').day()
  const lastDay = date.value.subtract(1, 'month').endOf('month').date()
  const count = firstDay - firstDayOfWeek // 前面有6天
  const preMonthDays: CalendarDateCell[] = Array.from({ length: count }).map(
    (_, idx) => {
      return {
        text: lastDay - (count - idx - 1),
        type: 'prev'
      }
    }
  )
  // 获取当前月有多少天
  const days = date.value.daysInMonth()
  // 当前月的天数
  const currentMonthDays: CalendarDateCell[] = Array.from({ length: days }).map(
    (_, idx) => {
      return {
        text: idx + 1,
        type: 'current'
      }
    }
  )
  list = [...preMonthDays, ...currentMonthDays]
  let remaning = 42 - list.length
  const nextMonthDays: CalendarDateCell[] = Array.from({
    length: remaning
  }).map((_, idx) => {
    return {
      text: idx + 1,
      type: 'next'
    }
  })
  list.push(...nextMonthDays)
  console.log(firstDay, lastDay, count, preMonthDays, days)
  return Array.from({ length: 6 }).map((_, idx) => {
    const start = idx * 7
    return list.slice(start, start + 7)
  })
})

const prevMonthDay = computed(() => date.value.subtract(1, 'month').date(1))
const nextMonthDay = computed(() => date.value.add(1, 'month').date(1))
const prevYearDay = computed(() => date.value.subtract(1, 'year').date(1))
const nextYearDay = computed(() => date.value.add(1, 'year').date(1))

function pickDay(day: Dayjs) {
  // 保存选中的
  selectDay.value = day
  emit('update:modelValue', day.toDate())
}
function selectDate(type: CalendarDateType) {
  const dateMap: Record<CalendarDateType, Dayjs> = {
    'prev-month': prevMonthDay.value,
    'prev-year': prevYearDay.value,
    'next-month': nextMonthDay.value,
    'next-year': nextYearDay.value,
    today: now
  }
  const day = dateMap[type]
  pickDay(day)
}

function formatter(text: number, type: CalendarDateCellType): Dayjs {
  switch (type) {
    case 'prev':
      return date.value.startOf('month').subtract(1, 'month').date(text)
    case 'current':
      return date.value.date(text)
    case 'next':
      return date.value.startOf('month').add(1, 'month').date(text)
  }
}

// 用于标识当前用户选择的是哪一个
const selectDay = ref<Dayjs>()
function handlePick({ text, type }: CalendarDateCell) {
  // 根据text和type获取日期来更新date
  const day = formatter(text, type)
  pickDay(day)
}

const getCellClass = ({ text, type }: CalendarDateCell) => {
  const clazz: string[] = [type]

  const date = formatter(text, type)
  if (date.isSame(selectDay.value, 'day')) {
    // 如果选中的日期和当前循环的日期相同就表示选中了
    clazz.push(bemDay.is('selected', true))
  }

  if (date.isSame(now, 'day')) {
    clazz.push(bemDay.is('today', true))
  }
  return clazz
}

const currentDate = computed(() => {
  return `${date.value.year()}年${
    date.value.month() + 1
  }月${date.value.date()}日`
})

function getSlotData({ text, type }: CalendarDateCell) {
  const date = formatter(text, type)
  return {
    isSelected: date.isSame(selectDay.value),
    day: date.format('YYYY-MM-DD'),
    date: date.toDate(),
    type
  }
}
</script>

<style scoped></style>
